import { ThreeElements, useFrame, useThree } from '@react-three/fiber'
import * as React from 'react'
import { DeviceOrientationControls as DeviceOrientationControlsImp } from './DeviceOrientationControls'
import { Camera, EventDispatcher, Event as ThreeEvent } from 'three'

/**
 * Utility type to declare the type of a `forwardRef` component so that the type is not "evaluated" in the declaration
 * file.
 */
export type ForwardRefComponent<P, T> = React.ForwardRefExoticComponent<React.PropsWithoutRef<P> & React.RefAttributes<T>>

declare module '@react-three/fiber' {
  interface ThreeElements {
    deviceOrientationControls: DeviceOrientationControlsImp
  }
}

export type DeviceOrientationControlsProps = Omit<ThreeElements['primitive'] & ThreeElements['deviceOrientationControls'] & {
  camera?: Camera
  onChange?: (e?: ThreeEvent) => void
  makeDefault?: boolean
}, 'object'>


export const DeviceOrientationControlsWrapper: ForwardRefComponent<
  DeviceOrientationControlsProps,
  DeviceOrientationControlsImp
> = React.forwardRef<DeviceOrientationControlsImp, DeviceOrientationControlsProps>(
  (props, ref) => {
    const { camera, onChange, makeDefault, ...rest } = props as DeviceOrientationControlsProps;
    const defaultCamera = useThree((state) => state.camera)
    const invalidate = useThree((state) => state.invalidate)
    const get = useThree((state) => state.get)
    const set = useThree((state) => state.set)
    const explCamera = camera || defaultCamera
    const controls = React.useMemo(() => new DeviceOrientationControlsImp(explCamera), [explCamera])

    React.useEffect(() => {
      const callback = (e: ThreeEvent) => {
        invalidate()
        if (onChange) onChange(e)
      }
      controls?.addEventListener?.('change', callback)
      return () => controls?.removeEventListener?.('change', callback)
    }, [onChange, controls, invalidate])

    useFrame(() => controls?.update(), -1)

    // console.log('y offset', rest.betaOffset)

    React.useEffect(() => {
      const current = controls
      current?.connect()
      return () => current?.dispose()
    }, [controls])

    React.useEffect(() => {
      if (makeDefault) {
        const old = get().controls
        set({ controls })
        return () => set({ controls: old })
      }
    }, [makeDefault, controls])

    return controls ? <primitive ref={ref} object={controls} {...rest} /> : null
  }
)